import type { DiscoveryTypeChip } from './discovery-chips';

const normalized = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export function refinementQuery(query: string, types: DiscoveryTypeChip[], type: string, audience: string, size: string, replaceCategory = false): string {
  // Category navigation starts afresh: a former model/brand must not poison the new category.
  let base = normalized(replaceCategory ? '' : query).replace(/\bmarim(?:e|ea)\s+[\w.,-]+(?:\s+eu)?\b/g, '').trim();
  if (type) {
    for (const item of [...types].sort((a, b) => b.key.length - a.key.length)) {
      const words = normalized(item.key).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      base = base.replace(new RegExp(`\\b${words}\\b`, 'g'), '');
    }
  }
  if (audience || type) base = base.replace(/\b(?:dama|femei|barbati|copii)\b/g, '');
  return [type, base, audience, size ? `marimea ${size}` : ''].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim();
}

export function refinementOptions(types: DiscoveryTypeChip[]) {
  return types.flatMap(type => {
    const audiences = type.audiences.filter(item => item.key);
    return (audiences.length ? audiences : [{key: '', label: ''}]).map(audience => ({
      value: JSON.stringify([type.key, audience.key]),
      type: type.key, audience: audience.key,
      label: audience.label && !normalized(type.label).includes(normalized(audience.label)) ? `${type.label} · ${audience.label}` : type.label,
    }));
  });
}

export function mountCompactRefinement(container: HTMLElement, types: DiscoveryTypeChip[], currentQuery: () => string, search: (query: string) => void): (query: string) => void {
  container.replaceChildren();
  container.className = 'sf-chipbox sf-refine';
  const makeSelect = (label: string) => {
    const select = document.createElement('select');
    select.setAttribute('aria-label', label);
    select.title = label;
    container.append(select);
    return select;
  };
  const type = makeSelect('Tip produs');
  const size = makeSelect('Mărime');
  const choices = refinementOptions(types);
  const options = (select: HTMLSelectElement, label: string, values: Array<{value: string; label: string}>) => {
    select.replaceChildren();
    for (const entry of [{value: '', label}, ...values]) {
      const option = document.createElement('option');
      option.value = entry.value;
      option.textContent = entry.label;
      select.append(option);
    }
  };
  const selected = () => choices.find(item => item.value === type.value);
  const updateSizes = () => {
    const choice = selected();
    const matching = choice ? types.filter(item => item.key === choice.type) : types;
    const values = matching.flatMap(item => item.audiences.filter(audience => !choice?.audience || audience.key === choice.audience).flatMap(audience => audience.sizes ?? []));
    const previous = size.value;
    options(size, 'Mărime', [...new Set(values)].sort((a, b) => a.localeCompare(b, 'ro', {numeric: true})).map(value => ({value, label: value})));
    if (values.includes(previous)) size.value = previous;
    size.disabled = !values.length;
  };
  options(type, 'Tip produs', choices);
  const apply = (replaceCategory = false) => {
    const choice = selected();
    const query = refinementQuery(currentQuery(), types, choice?.type ?? '', choice?.audience ?? '', size.value, replaceCategory);
    if (query) search(query);
  };
  type.addEventListener('change', () => { updateSizes(); apply(!!selected()); });
  size.addEventListener('change', () => apply());
  const sync = (query: string) => {
    const text = normalized(query);
    const matched = [...types].sort((a, b) => b.key.length - a.key.length).find(item => text.includes(normalized(item.key)));
    const audience = matched?.audiences.find(item => item.key && new RegExp(`\\b${item.key}\\b`).test(text))?.key ?? '';
    type.value = choices.find(item => item.type === matched?.key && item.audience === audience)?.value ?? '';
    updateSizes();
    const requested = text.match(/\bmarim(?:e|ea)\s+([\w.,-]+)/)?.[1]?.replace(',', '.');
    size.value = requested ? Array.from(size.options).find(item => item.value.toLowerCase() === requested)?.value ?? '' : '';
  };
  sync(currentQuery());
  return sync;
}
